import type { QueryObserverResult } from '@tanstack/react-query'
import { HttpStatusCode } from 'axios'
import type { AxiosError } from 'axios'

import {
  BasePessoaFisicaUseCase,
  type FetchResult,
} from '@/features/proposta/entities/Comprador/use-case/base-pessoa-fisica-use-case'
import type { TCompradoresCadastrados } from '@/features/proposta/interfaces/proposta.type'
import type { CompradorPessoaFisicaStepSchemaInput } from '@/features/proposta/schemas/compradores/comprador-pf.schema'
import { formatRadios } from '@/features/proposta/utils/format-radios'
import { origemTelefone } from '@/shared/interfaces/cliente.enums'
import type { TCliente } from '@/shared/interfaces/cliente.types'

export class CompradorPFUseCase extends BasePessoaFisicaUseCase<CompradorPessoaFisicaStepSchemaInput> {
  constructor(
    private readonly compradores: TCompradoresCadastrados,
    private readonly clienteAtendimento: { id: string; cpf?: string | null },
    private readonly emailsJaVinculados: Record<string, string>,
    private readonly nacionalidadePrincipalId: string,
    private readonly conjuges: TCompradoresCadastrados[number]['vinculos'],
  ) {
    super()
  }

  private obterPatchMenorIdade(cliente: TCliente, inativo?: boolean): CompradorPessoaFisicaStepSchemaInput {
    return {
      tipoDoCliente: cliente.tipoDoCliente,
      tipo: 'PessoaFisica',
      mode: 'create',
      dados: {
        id: inativo ? '' : cliente.id,
        cpf: cliente.cpf ?? '',
        nome: cliente.nome,
        profissaoId: cliente.profissao?.id ?? '',
        nacionalidadeId: cliente.nacionalidade?.id ?? this.nacionalidadePrincipalId,
        maiorDe18Anos: formatRadios(cliente.maiorDe18Anos),
        buscouCpf: !!cliente.cpf,
        email: cliente.email,
      },
      estadoCivil: null,
      contato: null,
      conjuge: null,
      emailsJaVinculados: this.emailsJaVinculados,
    }
  }

  private obterPatchMaiorIdade(cliente: TCliente, id?: boolean): CompradorPessoaFisicaStepSchemaInput {
    return {
      tipoDoCliente: cliente.tipoDoCliente,
      tipo: 'PessoaFisica',
      mode: 'create',
      dados: {
        id: id ? '' : cliente.id,
        cpf: cliente.cpf ?? '',
        nome: cliente.nome,
        profissaoId: cliente.profissao?.id ?? '',
        nacionalidadeId: cliente.nacionalidade?.id ?? this.nacionalidadePrincipalId,
        maiorDe18Anos: formatRadios(cliente.maiorDe18Anos),
        buscouCpf: !!cliente.cpf,
        email: null,
      },
      estadoCivil: {
        status: cliente.estadoCivil ?? undefined,
        regimeCasamento: cliente.regimeCasamento,
        tipoAssinatura: undefined,
        uniaoEstavel: formatRadios(cliente.possuiUniaoEstavel),
      },
      contato: {
        email: cliente.email ?? '',
        telefone: {
          origem: cliente.telefone?.origem ?? origemTelefone.Brasil,
          numero: cliente.telefone?.numero ?? '',
          principal: true,
        },
      },
      conjuge: null,
      emailsJaVinculados: this.emailsJaVinculados,
    }
  }

  interpretarResultadoBuscaManual(
    result: QueryObserverResult<TCliente, AxiosError>,
    compradorId?: string,
  ): FetchResult<Partial<CompradorPessoaFisicaStepSchemaInput>> {
    if (result.isError && result.error?.response?.status === HttpStatusCode.NotFound) {
      const shouldOpenModal = this.clienteAtendimento.id === compradorId && !this.clienteAtendimento.cpf
      return { type: 'NOT_FOUND', shouldOpenModal }
    }
    if (result.isSuccess && result.data) {
      const cliente = result.data
      const isMenor = cliente.maiorDe18Anos === false
      return {
        type: 'SUCCESS',
        patch: isMenor ? this.obterPatchMenorIdade(cliente) : this.obterPatchMaiorIdade(cliente),
      }
    }

    return { type: 'ERROR' }
  }

  interpretarResultadoBuscaAutomatica(
    result: QueryObserverResult<TCliente, AxiosError>,
    clienteInativo?: TCliente,
  ): FetchResult<Partial<CompradorPessoaFisicaStepSchemaInput>> {
    if (clienteInativo) {
      const isMenorInativo = clienteInativo.maiorDe18Anos === false
      return {
        type: 'INACTIVE',
        patch: isMenorInativo
          ? this.obterPatchMenorIdade(clienteInativo, true)
          : this.obterPatchMaiorIdade(clienteInativo, true),
      }
    }
    if (result.isSuccess && result.data) {
      const cliente = result.data
      const isMenor = cliente.maiorDe18Anos === false
      return {
        type: 'SUCCESS',
        patch: isMenor ? this.obterPatchMenorIdade(cliente) : this.obterPatchMaiorIdade(cliente),
      }
    }
    return { type: 'ERROR' }
  }

  validarCpfComprador(
    cpfRaw: string,
  ): { type: 'SUCCESS' } | { type: 'ERROR'; reason: 'IS_ALREADY_COMPRADOR' | 'IS_ALREADY_CONJUGE' } {
    if (this.conjuges.find((conjuge) => this.cpfIgual.bind(this)(conjuge.documento, cpfRaw)))
      return { reason: 'IS_ALREADY_CONJUGE', type: 'ERROR' }
    if (this.compradores.some((c) => this.cpfIgual(c.documento, cpfRaw)))
      return { type: 'ERROR', reason: 'IS_ALREADY_COMPRADOR' }
    return { type: 'SUCCESS' }
  }

  limparFormulario(): CompradorPessoaFisicaStepSchemaInput {
    return {
      tipoDoCliente: 'Contato',
      tipo: 'PessoaFisica',
      mode: 'create',
      dados: {
        cpf: '',
        nome: '',
        profissaoId: '',
        nacionalidadeId: this.nacionalidadePrincipalId,
        maiorDe18Anos: '',
        buscouCpf: false,
        email: null,
      },
      contato: {
        email: '',
        telefone: { numero: '', origem: origemTelefone.Brasil, principal: true },
      },
      estadoCivil: {
        regimeCasamento: null,
        status: null,
        tipoAssinatura: undefined,
        uniaoEstavel: undefined,
      },
      conjuge: null,
      emailsJaVinculados: this.emailsJaVinculados,
    }
  }
}
